import React, { useEffect, useRef, useState } from 'react';
import { Box, InputAdornment, TextField, Tooltip } from '@mui/material';

const TextFieldComponent = ({
  name,
  question,
  value,
  onChange,
  onNext,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  layoutVariant = 'default',
  readOnly = false,
}) => {
  const isAccountLayout = layoutVariant === 'account';
  const [error, setError] = useState('');
  const [rawValue, setRawValue] = useState(value || '');
  const [displayValue, setDisplayValue] = useState(value || '');
  const [hasValidated, setHasValidated] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const localInputRef = useRef();
  const fieldRef = inputRef || localInputRef;

  const isCellNumberField =
    name.toLowerCase().includes('phone') ||
    name.toLowerCase().includes('cell') ||
    name.toLowerCase() === 'contact_number';

  const validate = (candidateValue = rawValue) => {
    let isValid = true;
    const input = isCellNumberField
      ? String(candidateValue ?? '').replace(/\D/g, '')
      : candidateValue;
    const trimmedInput = String(input ?? '').trim();

    if (question.validation?.required && !trimmedInput) {
      setError(question.validation.message || 'Required');
      isValid = false;
    } else if (question.validation?.regex) {
      const regex = new RegExp(question.validation.regex);
      if (!regex.test(String(input ?? ''))) {
        setError(question.validation.message);
        isValid = false;
      } else {
        setError('');
      }
    } else {
      setError('');
    }

    setValidationState?.(name, isValid);
    return isValid;
  };

  const handleChange = (e) => {
    let input = e.target.value;
    if (question.onlyNumbers) {
      input = input.replace(/\D/g, '');
      if (isCellNumberField) {
        input = input.slice(0, 10);
      }
    }
    setRawValue(input);
    setDisplayValue(input);
    onChange?.(name, input);

    // Once the field has shown an error, keep it live so the message clears
    // immediately when the user fixes the value.
    if (hasValidated || error) {
      validate(input);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setDisplayValue(rawValue || '');
  };

  const handleBlur = () => {
    setIsFocused(false);
    setHasValidated(true);
    const isValid = validate();

    if (isValid && question.mask && typeof question.mask === 'string') {
      try {
        const maskFunc = new Function('input', question.mask);
        const cleanedInput = isCellNumberField
          ? rawValue.replace(/\D/g, '')
          : rawValue;
        const masked = maskFunc(cleanedInput);
        setDisplayValue(masked);
      } catch (err) {
        // ignore
      }
    }

    if (isValid && !question.disableAutoNext) {
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else {
        onNext?.();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setHasValidated(true);
      const isValid = validate();
      if (isValid) {
        if (nextFieldRef?.current) {
          nextFieldRef.current.focus();
        } else if (!question.disableAutoNext) {
          onNext?.();
        }
      }
    }
  };

  useEffect(() => {
    if (!hasValidated) return;
    validate(rawValue);
  }, [rawValue, hasValidated]);

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, () => validate());
    }
  }, [name, registerValidation, rawValue, hasValidated]);

  useEffect(() => {
    if (value && question.mask && typeof question.mask === 'string') {
      try {
        const maskFunc = new Function('input', question.mask);
        const cleanedInput = isCellNumberField
          ? value.replace(/\D/g, '')
          : value;
        const masked = maskFunc(cleanedInput);
        setDisplayValue(isFocused ? value : masked);
        setRawValue(value);
      } catch (err) {
        setDisplayValue(value);
        setRawValue(value);
      }
    } else {
      setDisplayValue(value || '');
      setRawValue(value || '');
    }
  }, [name, question.mask, value, isFocused]);

  const sizeOverrides = {
    'extra-small': '219px',
    small: '301px',
    medium: '403px',
    large: '534px',
    half: '500px',
    full: '1000px',
    'vehicle-return': '324px',
    'vehicle-return-full': '662px',
    'vehicle-full': '448px',
    'accounts-half': '416px',
    'accounts-full': '864px',
  };

  const fieldWidth = question.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';

  const fieldHeight = isMobile ? '48px' : '53px';

  const borderOverrides = question.tooltip?.message
    ? {
        '&:hover fieldset': {
          borderColor: '#E3E800',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#E3E800',
        },
      }
    : {};

  const handleClear = (e) => {
    e.preventDefault();
    setRawValue('');
    setDisplayValue('');
    setError('');
    onChange?.(name, '');
    fieldRef.current?.focus();
  };

  const startAdornment = question.startAdornment ? (
    <InputAdornment position="start" sx={{ mr: 0 }}>
      <Box
        component="span"
        sx={{
          color: '#738289',
          fontFamily: 'Inter, sans-serif',
          fontSize: '1rem',
        }}
      >
        {question.startAdornment}
      </Box>
    </InputAdornment>
  ) : null;

  const endAdornment =
    !readOnly && rawValue ? (
      <InputAdornment position="end" sx={{ ml: 0.5, mr: 0 }}>
        <span
          className="material-symbols-rounded"
          onMouseDown={handleClear}
          style={{
            fontWeight: 400,
            fontSize: 20,
            color: '#738289',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          close
        </span>
      </InputAdornment>
    ) : null;

  const textField = (
    <TextField
      name={name}
      fullWidth
      placeholder={question.placeholder}
      label={question.label}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      error={!!error}
      helperText={error}
      variant="outlined"
      margin="none"
      inputRef={fieldRef}
      type={
        question.onlyNumbers
          ? 'tel'
          : name.toLowerCase().includes('email')
            ? 'email'
            : 'text'
      }
      InputProps={{
        startAdornment,
        endAdornment,
        readOnly,
      }}
      InputLabelProps={{
        sx: {
          color: '#738289',
          // '&.Mui-focused': { color: '#738289' },
          // '&.Mui-error': { color: '#738289' },
        },
      }}
      sx={{
        width: isAccountLayout ? '100%' : isMobile ? '80vw' : fieldWidth,
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        maxWidth: '100%',
        height: fieldHeight,

        '& .MuiOutlinedInput-root': {
          borderRadius: '12px',
          height: 'auto',
          paddingTop: '16px',
          paddingRight: '12px',
          paddingBottom: '16px',
          paddingLeft: '12px',
          gap: '8px',
          borderWidth: '1px',
          '& input:focus::placeholder': {
            //Place holder when focused
            color: '#738289',
          },
          // Base border styling
          '& fieldset': {
            borderRadius: '12px',
            borderWidth: '1px',
            borderColor: '#A2ACB1',
          },
          //'&:hover fieldset': { borderColor: '#738289' },
          //'&.Mui-focused fieldset': { borderColor: '#738289' },
          //'&.Mui-error fieldset': { borderColor: '#738289' },

          // Input text + placeholder colors
          '& input': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '1rem',
            lineHeight: isMobile ? '100%' : '1.2rem',
            letterSpacing: '-0.02em',
            color: displayValue ? '#162F3B' : '#738289',
            padding: 0,
            '&::placeholder': {
              color: '#738289',
              opacity: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              lineHeight: '1.2rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },

          // Keep your tooltip-specific overrides LAST so they win
          ...borderOverrides,
        },

        '& .MuiFormHelperText-root': {
          textAlign: 'right',
          fontFamily: 'Inter, sans-serif',
          fontSize: isMobile ? '0.6rem' : '0.75rem',
          color: '#D32F2F',
          mt: '0px',
          mx: 0,
          pr: '8px',
        },
      }}
    />
  );

  return question.tooltip?.message ? (
    <Tooltip
      title={<span>{question.tooltip.message}</span>}
      placement="right"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '-0.002em',
            borderRadius: '12px',
            border: '2px solid #E3E800',
            backgroundColor: '#FFFFFF',
            padding: '16px',
            boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
            maxWidth: '297px',
            color: '#000',
            '& .MuiTooltip-arrow': {
              color: '#E3E800',
            },
          },
        },
        arrow: {
          sx: {
            color: '#E3E800',
          },
        },
      }}
    >
      {textField}
    </Tooltip>
  ) : (
    textField
  );
};

export default TextFieldComponent;
